import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { AdminLayout } from '../../components/AdminLayout';
import { Download, Check, X, Search, Phone, Mail, MapPin } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface QuoteRequest {
  id: string;
  first_name: string;
  last_name: string;
  company_name?: string; // Added field
  phone: string;
  email: string;
  city: string;
  message: string;
  car_model_name: string;
  car_brand_name: string;
  status: 'pending' | 'processed' | 'rejected';
  created_at: string;
}

export function QuoteRequests() {
  const [requests, setRequests] = useState<QuoteRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('quote_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (error) {
      console.error('Error fetching quote requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, status: 'processed' | 'rejected') => {
    try {
      const { error } = await supabase
        .from('quote_requests')
        .update({ status })
        .eq('id', id);

      if (error) throw error;
      
      // Update local state
      setRequests(requests.map(r => r.id === id ? { ...r, status } : r));
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const generatePDF = (request: QuoteRequest) => {
    const doc = new jsPDF();
    
    // -- PDF DESIGN SETUP --
    const primaryColor = [213, 70, 44]; // #D5462E (Your Brand Red)
    const secondaryColor = [41, 41, 41]; // #292929 (Dark Gray)

    // Header Background
    doc.setFillColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
    doc.rect(0, 0, 210, 40, 'F');

    // Logo / Title
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('ARGUS TUNISIE', 20, 20);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('DEMANDE DE DEVIS OFFICIELLE', 20, 28);

    // Date
    doc.setFontSize(10);
    doc.text(`Date: ${new Date(request.created_at).toLocaleDateString('fr-FR')}`, 150, 20);
    doc.text(`Réf: ${request.id.slice(0, 8).toUpperCase()}`, 150, 26);

    // Section 1: Client Information
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('INFORMATIONS CLIENT', 20, 55);
    doc.setDrawColor(200, 200, 200);
    doc.line(20, 58, 190, 58); // Horizontal Line

    // Prepare client info body
    const clientInfoBody = [
      ['Nom complet', `${request.first_name} ${request.last_name}`],
      ['Téléphone', request.phone],
      ['Email', request.email],
      ['Ville', request.city],
    ];

    // Add company name if it exists
    if (request.company_name) {
      clientInfoBody.splice(1, 0, ['Entreprise', request.company_name]);
    }

    // Client Data Table
    autoTable(doc, {
      startY: 65,
      head: [],
      body: clientInfoBody,
      theme: 'plain',
      styles: { fontSize: 10, cellPadding: 2 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 }, 1: { cellWidth: 100 } },
    });

    // Section 2: Vehicle Interest
    // Get Y position after previous table
    const finalY = (doc as any).lastAutoTable.finalY + 15;

    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('INTÉRÊT VÉHICULE', 20, finalY);
    doc.line(20, finalY + 3, 190, finalY + 3);

    // REMOVED 'Marque' column as requested
    autoTable(doc, {
      startY: finalY + 10,
      head: [['Modèle / Version', 'Type de demande']],
      body: [
        [
          request.car_model_name || 'N/A', 
          'Demande de Devis / Prix'
        ]
      ],
      theme: 'grid',
      headStyles: { fillColor: primaryColor, textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 10, cellPadding: 4 },
    });

    // Section 3: Message / Notes
    const messageY = (doc as any).lastAutoTable.finalY + 15;
    
    if (request.message) {
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('MESSAGE DU CLIENT', 20, messageY);
      doc.line(20, messageY + 3, 190, messageY + 3);

      doc.setTextColor(60, 60, 60);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      
      // Split text to fit page width
      const splitText = doc.splitTextToSize(request.message, 170);
      doc.text(splitText, 20, messageY + 12);
    }

    // Footer
    const pageHeight = doc.internal.pageSize.height;
    doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.setLineWidth(1);
    doc.line(20, pageHeight - 20, 190, pageHeight - 20);
    
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.text('Argus Tunisie - Le spécialiste de la cotation automobile en Tunisie', 105, pageHeight - 15, { align: 'center' });
    doc.text('www.argusautomobile.com', 105, pageHeight - 10, { align: 'center' });

    // Save PDF
    doc.save(`Devis_${request.first_name}_${request.last_name}_${request.car_model_name}.pdf`);
  };

  const filteredRequests = requests.filter(r => 
    r.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.phone.includes(searchTerm) ||
    r.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (r.company_name && r.company_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <AdminLayout currentPage="quote-requests">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Demandes de Devis</h1>
        <p className="text-gray-500">Gérez les demandes de devis des utilisateurs.</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50/50">
          <div className="relative max-w-sm w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher (nom, entreprise, email)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C] focus:border-transparent"
            />
          </div>
          <div className="text-sm text-gray-500">
            {filteredRequests.length} demande{filteredRequests.length !== 1 ? 's' : ''}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client / Entreprise</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Véhicule</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">Chargement...</td>
                </tr>
              ) : filteredRequests.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">Aucune demande trouvée</td>
                </tr>
              ) : (
                filteredRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{request.first_name} {request.last_name}</div>
                      {request.company_name && (
                        <div className="text-xs font-bold text-[#D4452C] mt-0.5">{request.company_name}</div>
                      )}
                      <div className="text-xs text-gray-500 flex items-center mt-1"><MapPin className="h-3 w-3 mr-1" /> {request.city}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-semibold">{request.car_brand_name}</div>
                      <div className="text-xs text-gray-500">{request.car_model_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        <span className="text-xs text-gray-600 flex items-center"><Phone className="h-3 w-3 mr-1" /> {request.phone}</span>
                        <span className="text-xs text-gray-600 flex items-center"><Mail className="h-3 w-3 mr-1" /> {request.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        request.status === 'processed' ? 'bg-green-100 text-green-800' :
                        request.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {request.status === 'processed' ? 'Traité' : request.status === 'rejected' ? 'Rejeté' : 'En attente'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(request.created_at).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button 
                          onClick={() => generatePDF(request)}
                          className="text-blue-600 hover:text-blue-900 bg-blue-50 p-2 rounded-lg transition-colors"
                          title="Télécharger PDF"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        {request.status === 'pending' && (
                          <>
                            <button 
                              onClick={() => updateStatus(request.id, 'processed')}
                              className="text-green-600 hover:text-green-900 bg-green-50 p-2 rounded-lg transition-colors"
                              title="Marquer comme traité"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button 
                              onClick={() => updateStatus(request.id, 'rejected')}
                              className="text-red-600 hover:text-red-900 bg-red-50 p-2 rounded-lg transition-colors"
                              title="Rejeter"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}